import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar, GridRowParams } from "@mui/x-data-grid";
import { API } from "aws-amplify";
import { listLLMProjects } from "../../graphql/queries";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

const columns: GridColDef[] = [
  { field: "id", headerName: "ID", width: 150 },
  { field: "name", headerName: "Project", width: 200 },
  { field: "status", headerName: "Status", width: 150 },
  { field: "users", headerName: "Users", width: 75,
    valueGetter: (value, row) => row.users.items.length ,
   },

   { field: "services", headerName: "Services", width: 75,
    valueGetter: (value, row) => row.services.items.length ,
   },

   { field: "subscription", headerName: "Subscription", width: 75,
    valueGetter: (value, row) => (row.subscription?.subscriptionStatus || "")
   },
  
   { field: "trialDays", headerName: "Trial Days Remaining", width: 150,
    valueGetter: (value, row) => {
      const createdAt = new Date(row.createdAt);
      const diffMs = Date.now() - createdAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      return 30 - diffDays;
    }
   },


  { field: "createdAt", headerName: "Created At", width: 200,
    valueFormatter: (value) => {
      const date = new Date(value);
      return date.toISOString();
    }
   },
];

export default function AdminProjects() {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [nextTokenMap, setNextTokenMap] = useState<Record<number, string | null>>({ 0: null });
  const [selectedProject, setSelectedProject] = useState<any>(null); // new state

  const { 
    data, 
    isPending, 
    isError, 
    error, 
    isFetching, 
    isPlaceholderData 
  } = useQuery({
    queryKey: ['projects', paginationModel],
    queryFn: async () => {
      const result: any = await API.graphql({
        query: listLLMProjects,
        variables: {
          limit: paginationModel.pageSize,
          nextToken: nextTokenMap[paginationModel.page] || null,
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      
      const { items, nextToken, total } = result.data.listLLMProjects;
      
      // Store the nextToken for the next page
      if (nextToken) {
        setNextTokenMap(prev => ({
          ...prev,
          [paginationModel.page + 1]: nextToken
        }));
      }
      
      return { 
        items, 
        total,
        hasMore: !!nextToken 
      };
    },
    placeholderData: keepPreviousData,
  });

  const handlePaginationModelChange = (newModel: {page: number, pageSize: number}) => {
    // If changing to a page that doesn't exist in our token map and it's not the first page,
    // we can't jump directly there - this is a limitation of nextToken-based pagination
    if (newModel.page > paginationModel.page + 1 || 
       (newModel.page > 0 && !nextTokenMap[newModel.page])) {
      console.warn("Can't skip pages with nextToken pagination");
      return;
    }
    
    // If changing page size, reset to first page
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({
        page: 0,
        pageSize: newModel.pageSize
      });
      // Reset nextToken map when changing page size
      setNextTokenMap({ 0: null });
    } else {
      setPaginationModel(newModel);
    }
  };

  console.log("Selected project:", selectedProject);

  return (
    <Box>
      <Typography variant="h5">Projects Management</Typography>
      {isError ? (
        <Typography color="error">Error: {(error as Error).message}</Typography>
      ) : (
        <DataGrid
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          rows={data?.items || []}
          columns={columns}
          pagination
          loading={isPending || isFetching}
          pageSizeOptions={[10, 20, 30, { value: -1, label: "All" }]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={data?.total || 0}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          onRowClick={(params: GridRowParams) => setSelectedProject(params.row)} // new handler
        />
      )}
      {isFetching && !isPending && <Typography sx={{ mt: 1 }}>Fetching...</Typography>}
      
      {selectedProject && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">
            User Emails for {selectedProject.name}
          </Typography>
          <ul>
            {selectedProject.users?.items?.map((user: any) => (
              <li key={user.id}>{user.email} - {user.displayName}</li>
            ))}
          </ul>

          
        </Box>
      )}
    </Box>
  );
}
